import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Button, Form, Tooltip, Typography } from 'antd';
import { AimOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { spacing } from '@goelectric/ui-components';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapContainer, Marker, useMap, useMapEvents } from 'react-leaflet';
import type { LocationFormState } from '../utils/locationForm';
import { resolveMapTarget } from '../utils/mapCenter';
import { pinIcon } from '../utils/mapPin';
import { OsmTileLayer } from './OsmTileLayer';

/** Registra el click sobre el mapa y delega las coordenadas al host. */
function MapClickHandler({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(event) {
      onPick(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

/**
 * Reposiciona la vista cuando cambia el centro calculado (pin/zona). **No toca el marcador
 * ni las coordenadas del form**: `setView` sólo mueve la cámara. Así el mapa queda robusto
 * si algún día se deja montado entre pasos (hoy se desmonta, así que `center` inicial casi
 * siempre alcanza).
 */
function CenterOnTarget({ lat, lng, zoom }: { lat: number; lng: number; zoom: number }) {
  const map = useMap();
  // `setView` mueve la cámara; NO escribe latitude/longitude. Ver nota en LocationMapPicker.
  useEffect(() => {
    map.setView([lat, lng], zoom);
  }, [map, lat, lng, zoom]);
  return null;
}

/**
 * Redondeo a 5 decimales (~1.1 m): 6 decimales son ~11 cm, ruido por debajo de la
 * precisión útil de una ubicación. Así el valor guardado coincide con el mostrado.
 */
function roundCoordinate(value: number): number {
  return Math.round(value * 1e5) / 1e5;
}

/**
 * Picker de coordenadas sobre un mapa (paso 3 del alta / modal de edición).
 *
 * Es **bidireccional** con los `InputNumber` de lat/long del form: escucha `latitude`/
 * `longitude` (si el operador tipea, el pin se mueve) y escribe esos mismos campos al
 * arrastrar el pin o hacer click en el mapa.
 *
 * ⚠️ **Centrar el mapa NUNCA escribe `latitude`/`longitude`.** Las coordenadas cambian
 * únicamente por click en el mapa, arrastre del pin o tipeo en los inputs. Si al entrar al
 * paso el centrado seteara coordenadas, toda ubicación nacería con un punto inventado.
 */
export function LocationMapPicker() {
  const form = Form.useFormInstance<LocationFormState>();
  const latitude = Form.useWatch('latitude', form);
  const longitude = Form.useWatch('longitude', form);
  const state = Form.useWatch('state', form);
  const city = Form.useWatch('city', form);

  const target = useMemo(
    () => resolveMapTarget({ latitude, longitude, state, city }),
    [latitude, longitude, state, city],
  );

  const position: [number, number] | null =
    typeof latitude === 'number' && typeof longitude === 'number' ? [latitude, longitude] : null;

  const mapRef = useRef<L.Map>(null);

  const setCoordinates = useCallback(
    (lat: number, lng: number) => {
      form.setFieldsValue({ latitude: roundCoordinate(lat), longitude: roundCoordinate(lng) });
    },
    [form],
  );

  return (
    // `marginBottom` = espacio.6 (24px), el mismo ritmo vertical que separa los campos dentro
    // de cada paso del wizard (antd `Form.Item` = `marginLG`): así la fila de Latitud/Longitud
    // no queda pegada al borde inferior del mapa.
    <div style={{ marginTop: 8, marginBottom: spacing['6'].$value }}>
      <div
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}
      >
        <div>
          <Typography.Text strong>Ubica el punto en el mapa</Typography.Text>
          <div>
            <Typography.Text type="secondary">
              Arrastra el pin o haz clic para ajustarlo
            </Typography.Text>
          </div>
        </div>
        <Tooltip title="Centrar en la zona">
          <Button
            size="small"
            icon={<AimOutlined />}
            aria-label="Centrar en la zona"
            disabled={!target.zone}
            onClick={() =>
              target.zone && mapRef.current?.setView(target.zone.center, target.zone.zoom)
            }
          />
        </Tooltip>
      </div>

      <div
        className="locaciones-map"
        role="region"
        aria-label="Mapa para ubicar el punto exacto de la ubicación"
      >
        <MapContainer
          ref={mapRef}
          className="locaciones-map__canvas"
          center={target.center}
          zoom={target.zoom}
          scrollWheelZoom
          style={{ height: 320, borderRadius: 8, marginTop: 8 }}
        >
          <OsmTileLayer />
          <CenterOnTarget
            lat={target.center[0]}
            lng={target.center[1]}
            zoom={target.zoom}
          />
          <MapClickHandler onPick={setCoordinates} />
          {position && (
            <Marker
              draggable
              position={position}
              icon={pinIcon()}
              eventHandlers={{
                dragend: (event) => {
                  const { lat, lng } = (event.target as L.Marker).getLatLng();
                  setCoordinates(lat, lng);
                },
              }}
            />
          )}
        </MapContainer>

        {/* Panel glass: agrupa lo que hoy está disperso ("dónde está esto"). No captura
            clicks (pointer-events: none) y vive por encima de los panes de Leaflet. */}
        <div className="locaciones-map__info">
          <div className="locaciones-map__info-row">
            <EnvironmentOutlined aria-hidden />
            {state ? (
              <span>
                {city && <strong>{city}</strong>}
                {city && ' · '}
                <span className="locaciones-map__info-muted">{state}</span>
              </span>
            ) : (
              <span className="locaciones-map__info-muted">
                Elige departamento y municipio para centrar el mapa
              </span>
            )}
          </div>
          <div className="locaciones-map__info-row locaciones-map__info-row--coords">
            <AimOutlined aria-hidden />
            {position ? (
              <span className="locaciones-map__info-coords">
                <span className="locaciones-map__info-muted">Lat</span>{' '}
                <span>{position[0].toFixed(5)}</span>
                {' · '}
                <span className="locaciones-map__info-muted">Lng</span>{' '}
                <span>{position[1].toFixed(5)}</span>
              </span>
            ) : (
              <span className="locaciones-map__info-muted">Sin coordenadas marcadas</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
